import React, { useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { CheckCircleIcon } from '../components/CheckCircleRow';
import { HomepageImageGlowWrap } from '../components/HomepageImageGlow';
import { homepageSectionImagePlaceholderStretchClassName } from '../constants/homepageImagePlaceholder';
import {
  PIXANO_APPLE_STORE_URL,
  PIXANO_GOOGLE_PLAY_URL,
  homepageElevatedPanelClassName,
  homepagePrimaryButtonClassName,
  homepageSectionContainerClassName,
  homepageSectionPaddingYClassName,
  sectionHeadingAccentCenterClassName,
  sectionHeadingAccentClassName,
  storeButtonClassName,
} from '../constants/homepageUi';
import { USER_PORTAL_URL } from '../config/portals';

const DEFAULT_DOCUMENT_TITLE = 'Pixano – Training';

/** Hero secondary CTA — matches For Businesses contributor/login intent separation. */
const contributorsHeroSecondaryButtonClassName =
  'inline-flex items-center justify-center rounded-lg border border-white/25 bg-transparent px-6 py-3 text-sm font-medium text-white/90 transition-all hover:border-[#00e3ff] hover:bg-[rgba(0,227,255,0.08)] hover:text-white hover:shadow-[0_0_14px_rgba(0,227,255,0.2)]';

/** Contributors hero visual — same treatment as For Businesses / homepage hero. */
const contributorsHeroImageClassName =
  'w-full h-auto max-w-full rounded-2xl shadow-[0_8px_48px_rgba(0,0,0,0.35)]';

/** Split-section imagery — matches For Businesses “What We Offer” blocks. */
const contributorsSplitImageClassName =
  'w-full h-auto max-w-full rounded-2xl shadow-[0_8px_48px_rgba(0,0,0,0.35)]';

const EARN_BULLETS: string[] = [
  'Complete guided photo and video tasks in the Pixano app.',
  'Uploads go through review before they count toward your history.',
  'Approved submissions earn in-app Pixano rewards—not cash payouts.',
  'Your contributions help improve AI systems trained on real homes, food, handwriting, and everyday environments.',
];

const HISTORY_BULLETS: string[] = [
  'Download approved images with structured labels and metadata',
  'Use your data for personal AI models or experiments',
  'Capture real-world conditions—not synthetic datasets',
  'Build training data without needing annotation tools or pipelines',
];

const HOW_STEPS: { label: string; title: string; body: string }[] = [
  {
    label: 'Step 1',
    title: 'Create an account or log in',
    body: 'Sign up in the Pixano app or user portal so tasks, rewards, and your approved data history stay connected to your profile.',
  },
  {
    label: 'Step 2',
    title: 'Complete tasks and submit data',
    body: 'Choose tasks that fit your comfort level—rooms, food, handwriting, and more—then submit captures for review.',
  },
  {
    label: 'Step 3',
    title: 'Earn rewards and review approved history',
    body: 'When submissions are approved, you earn in-app rewards and can access your approved data history on your terms.',
  },
];

const CONTROL_BULLETS: string[] = [
  'You choose which tasks to take on and what to upload.',
  'Pause or stop using Pixano whenever you want.',
  'Your approved data history remains associated with your account—not broadcast as a public profile.',
  'We keep expectations clear: rewards are in-app and tied to approved work—not a side income.',
];

function SplitSection({
  title,
  intro,
  bullets,
  imageSide,
  imageSrc,
  imageAlt,
}: {
  title: string;
  intro: string;
  bullets: string[];
  imageSide: 'left' | 'right';
  imageSrc?: string;
  imageAlt?: string;
}) {
  const rowAlign = imageSrc ? 'lg:items-center' : 'lg:items-stretch';
  const rowClasses =
    imageSide === 'right'
      ? `flex flex-col gap-9 lg:flex-row-reverse ${rowAlign} lg:gap-12 xl:gap-16`
      : `flex flex-col gap-9 lg:flex-row ${rowAlign} lg:gap-12 xl:gap-16`;

  return (
    <div className={rowClasses}>
      <div className="flex min-h-0 w-full min-w-0 flex-col self-stretch lg:flex-1 lg:basis-0">
        <HomepageImageGlowWrap
          className={imageSrc ? 'w-full max-w-full' : 'min-h-[420px] flex-1'}
        >
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={imageAlt ?? ''}
              className={contributorsSplitImageClassName}
              decoding="async"
            />
          ) : (
            <div className={homepageSectionImagePlaceholderStretchClassName} aria-hidden />
          )}
        </HomepageImageGlowWrap>
      </div>
      <div className="flex w-full min-w-0 max-w-xl flex-col justify-center py-1 lg:max-w-[36rem] lg:flex-1 lg:basis-0 lg:py-2 xl:pr-4">
        <h2
          className={`mb-3.5 inline-block text-2xl font-bold tracking-tight text-white sm:mb-4 sm:text-3xl lg:text-4xl ${sectionHeadingAccentClassName}`}
        >
          {title}
        </h2>
        <p className="mb-6 text-base leading-relaxed text-white/85 sm:text-lg">{intro}</p>
        <ul className="space-y-3.5 sm:space-y-4">
          {bullets.map((item) => (
            <li key={item} className="flex gap-3.5 sm:gap-4 items-start">
              <CheckCircleIcon className="mt-0.5" />
              <span className="text-base leading-relaxed text-white/90 sm:text-lg">{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const ForUsers: React.FC = () => {
  useEffect(() => {
    document.title = 'For Contributors — Pixano';
    return () => {
      document.title = DEFAULT_DOCUMENT_TITLE;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F14]">
      <Navigation />
      <main>
        {/* Hero */}
        <section className="bg-[#0B0F14] pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className={homepageSectionContainerClassName}>
            <div className="flex flex-col lg:flex-row lg:items-stretch gap-12 lg:gap-14 xl:gap-16">
              <div className="w-full lg:w-[40%] lg:max-w-[40%] flex flex-col justify-center lg:pr-4">
                <h1 className="text-4xl sm:text-5xl lg:text-[2.75rem] xl:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-8">
                  Contribute Real-World Data. Turn Photos Into AI-Ready Data.
                </h1>
                <p className="text-base sm:text-lg text-white/90 leading-relaxed mb-10">
                  Capture everyday photos and videos that help train AI systems on real homes, food, handwriting, and
                  daily environments. Earn in-app rewards for approved work—and download structured, labeled data for
                  your own AI projects when you&apos;re ready.
                </p>
                <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                  <a href="#contributors-download-app" className={homepagePrimaryButtonClassName}>
                    Download the App
                  </a>
                  <a href={`${USER_PORTAL_URL}/login`} className={contributorsHeroSecondaryButtonClassName}>
                    Login to User Portal
                  </a>
                </div>
              </div>
              <div className="w-full lg:w-[60%] lg:max-w-[60%] flex justify-center lg:justify-end lg:items-center">
                <HomepageImageGlowWrap className="w-full max-w-full">
                  <img
                    src="/assets/hero-users.png"
                    alt="Contributors capturing real-world photos and video for AI training data"
                    className={contributorsHeroImageClassName}
                    decoding="async"
                  />
                </HomepageImageGlowWrap>
              </div>
            </div>
          </div>
        </section>

        {/* Section A */}
        <section
          className={`scroll-mt-20 border-t border-[rgba(255,255,255,0.05)] bg-[#0B0F14] ${homepageSectionPaddingYClassName}`}
        >
          <div className={homepageSectionContainerClassName}>
            <SplitSection
              imageSide="left"
              title="Earn Rewards for Real Contributions"
              intro="Turn everyday photos and videos into structured contributions—reviewed, approved, and rewarded in-app."
              bullets={EARN_BULLETS}
              imageSrc="/assets/earn-users.png?v=3"
              imageAlt="Earn in-app rewards for approved photo and video contributions"
            />
          </div>
        </section>

        {/* Section B */}
        <section
          className={`scroll-mt-20 border-t border-[rgba(255,255,255,0.05)] bg-[#0B0F14] ${homepageSectionPaddingYClassName}`}
        >
          <div className={homepageSectionContainerClassName}>
            <SplitSection
              imageSide="right"
              title="Turn Your Photos Into AI-Ready Data"
              intro="Every approved submission is reviewed, structured, and labeled—so you don't just upload data, you get usable training data back. Download your approved images and annotations anytime and use them for your own AI projects, experiments, or future tools."
              bullets={HISTORY_BULLETS}
              imageSrc="/assets/turn-users.png"
              imageAlt="Download structured, labeled training data from your approved submissions"
            />
          </div>
        </section>

        {/* Section C — How it works + download CTA */}
        <section
          id="how-it-works"
          className={`scroll-mt-20 border-t border-[rgba(255,255,255,0.05)] bg-[#0B0F14] pt-14 pb-8 lg:pt-[4.5rem] lg:pb-10`}
        >
          <div className={`${homepageSectionContainerClassName} flex flex-col items-center`}>
            <h2
              className={`text-center text-3xl font-bold tracking-tight text-white sm:text-4xl mb-4 ${sectionHeadingAccentCenterClassName} inline-block`}
            >
              How It Works
            </h2>
            <p className="mb-10 max-w-2xl text-center text-sm leading-relaxed text-white/60 sm:mb-11 sm:text-[15px]">
              A simple flow from signup to approved contributions—no confusing promises, just clear steps.
            </p>
            <div className="grid w-full max-w-[1000px] grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
              {HOW_STEPS.map((step) => (
                <div
                  key={step.label}
                  className={`${homepageElevatedPanelClassName} flex h-full flex-col p-5 transition-colors duration-200 sm:p-6 md:hover:border-[rgba(0,227,255,0.22)]`}
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#00e3ff]/70">{step.label}</p>
                  <p className="mt-3 text-[15px] font-semibold tracking-tight text-white sm:text-base">{step.title}</p>
                  <p className="mt-2.5 text-sm leading-relaxed text-white/60 sm:mt-3">{step.body}</p>
                </div>
              ))}
            </div>

            <div
              id="contributors-download-app"
              className="scroll-mt-20 mt-12 flex w-full max-w-[1000px] flex-col items-center text-center sm:mt-14"
              aria-labelledby="contributors-cta-download-heading"
            >
              <h2
                id="contributors-cta-download-heading"
                className={`mb-3 max-w-xl text-2xl font-bold tracking-tight text-white sm:text-3xl ${sectionHeadingAccentCenterClassName} inline-block`}
              >
                Ready to start contributing?
              </h2>
              <p className="mb-8 max-w-lg text-sm leading-relaxed text-white/65 sm:mb-9 sm:text-[15px]">
                Download the Pixano app to browse tasks, capture data, and track rewards.
              </p>
              <div className="flex w-full max-w-md flex-col gap-2 sm:max-w-none sm:flex-row sm:justify-center sm:gap-3">
                <a
                  href={PIXANO_APPLE_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${storeButtonClassName} w-full justify-center sm:w-auto`}
                >
                  Apple Store
                </a>
                <a
                  href={PIXANO_GOOGLE_PLAY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${storeButtonClassName} w-full justify-center sm:w-auto`}
                >
                  Google Play
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Section D — You Stay in Control */}
        <section
          className={`scroll-mt-20 border-t border-[rgba(255,255,255,0.05)] bg-[#0B0F14] ${homepageSectionPaddingYClassName}`}
        >
          <div className={`${homepageSectionContainerClassName} flex flex-col items-center`}>
            <h2
              className={`text-center text-3xl font-bold tracking-tight text-white sm:text-4xl mb-4 ${sectionHeadingAccentCenterClassName} inline-block`}
            >
              You Stay in Control
            </h2>
            <p className="mb-10 max-w-2xl text-center text-sm leading-relaxed text-white/60 sm:mb-11 sm:text-[15px]">
              Contributors decide how far to go. Pixano is built around consent, clarity, and account-level history—not
              hype.
            </p>
            <ul className="w-full max-w-2xl space-y-3.5 sm:space-y-4">
              {CONTROL_BULLETS.map((item) => (
                <li key={item} className="flex gap-3.5 sm:gap-4 items-start">
                  <CheckCircleIcon className="mt-0.5 shrink-0" />
                  <span className="text-base leading-relaxed text-white/90 sm:text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* CTA — portal login */}
        <section
          className={`scroll-mt-20 border-t border-[rgba(255,255,255,0.05)] bg-[#0B0F14] ${homepageSectionPaddingYClassName}`}
          aria-labelledby="contributors-cta-portal-heading"
        >
          <div className={`${homepageSectionContainerClassName} flex flex-col items-center text-center`}>
            <h2
              id="contributors-cta-portal-heading"
              className={`mb-3 max-w-xl text-2xl font-bold tracking-tight text-white sm:text-3xl ${sectionHeadingAccentCenterClassName} inline-block`}
            >
              Already have an account?
            </h2>
            <p className="mb-8 max-w-lg text-sm leading-relaxed text-white/65 sm:mb-9 sm:text-[15px]">
              Log in to the user portal to manage your profile, rewards, and approved data access.
            </p>
            <a href={`${USER_PORTAL_URL}/login`} className={homepagePrimaryButtonClassName}>
              Login to User Portal
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ForUsers;
